import { useState } from "react";
import { type Control, Controller, useWatch } from "react-hook-form";

import useCrmSources from "features/integrations/datahooks/useCrmSources";

import { MenuSearchInput } from "common/components/MenuSearchInput";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import FormField from "common/components/ui/FormField";
import SelectButton from "common/components/ui/SelectButton";
import { HTTPError } from "common/helpers/HTTP";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import type { CrmSourceSchemaType } from "../../../schemas";

interface SourceFormFieldProps {
  control: Control<CrmSourceSchemaType>;
}

export default function SourceFormField({ control }: SourceFormFieldProps) {
  const isDesktop = useTwBreakpoint("lg");

  const type = useWatch({ control, name: "sourceType" });

  const [inputValue, setInputValue] = useState("");

  const sourceType = type === "instantly" ? "lists" : "campaigns";

  const { crmSources, iseLoadingCrmSources, crmSourcesError } = useCrmSources({
    type,
    sourceType,
  });

  const filteredOptions = crmSources
    ? crmSources.filter((option) =>
        option.name.toLowerCase().includes(inputValue.toLowerCase()),
      )
    : [];

  return (
    <Controller
      control={control}
      name="sourceId"
      render={({ field, fieldState }) => {
        const selectedSource = crmSources
          ? crmSources.find(({ id }) => id === field.value)
          : undefined;

        let errorMessage: string | undefined;
        if (crmSourcesError) {
          errorMessage =
            crmSourcesError instanceof HTTPError
              ? crmSourcesError.serverMessage
              : "Error loading crm sources, please try again later";
        } else {
          errorMessage = fieldState.error?.message;
        }

        const hasError = !!crmSourcesError || !!fieldState.error;

        return (
          <FormField
            label="Select a source"
            htmlFor="crm-source-select"
            hint="Choose which targeting source to pull contacts from"
            error={errorMessage}
          >
            <DropdownMenu>
              <DropdownMenuTrigger disabled={!type} asChild>
                <SelectButton
                  size={isDesktop ? "md" : "lg"}
                  id="crm-source-select"
                  hasSelection={!!field.value}
                  isLoading={iseLoadingCrmSources}
                  hasError={hasError}
                  className="w-full"
                >
                  {selectedSource
                    ? selectedSource.name
                    : "Select a targeting source"}
                </SelectButton>
              </DropdownMenuTrigger>

              <DropdownMenuContent className="w-[var(--radix-popper-anchor-width)]">
                {crmSources?.length > 4 && (
                  <MenuSearchInput
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                  />
                )}
                <DropdownMenuList>
                  {filteredOptions.map(({ id, name }) => (
                    <DropdownMenuItem
                      key={id}
                      onSelect={() => field.onChange(id)}
                    >
                      {name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuList>
              </DropdownMenuContent>
            </DropdownMenu>
          </FormField>
        );
      }}
    />
  );
}
